// Backend: GET /api/expenses

export type ExpenseCategory = 'feed' | 'medicine' | 'utilities' | 'labor' | 'equipment' | 'transport' | 'biosecurity' | 'maintenance' | 'other';
export type ExpenseStatus = 'pending' | 'approved' | 'rejected' | 'paid';

export interface ExpenseRecord {
  id: string;
  expenseNumber: string;
  date: string;
  farm: string;
  branch: string;
  category: ExpenseCategory;
  description: string;
  vendor: string;
  quantity?: number;
  unit?: string;
  unitCost?: number;
  amount: number;
  paymentMethod: string;
  receiptRef: string;
  approvedBy: string;
  status: ExpenseStatus;
  notes: string;
  recordedBy: string;
}

export const EXPENSE_CATEGORIES: { value: ExpenseCategory; label: string; color: string; bg: string }[] = [
  { value: 'feed',        label: 'Feed & Nutrition',    color: 'text-amber-700',  bg: 'bg-amber-100' },
  { value: 'medicine',    label: 'Medicine & Vaccines', color: 'text-red-700',    bg: 'bg-red-100' },
  { value: 'utilities',   label: 'Utilities',           color: 'text-blue-700',   bg: 'bg-blue-100' },
  { value: 'labor',       label: 'Labor & Wages',       color: 'text-purple-700', bg: 'bg-purple-100' },
  { value: 'equipment',   label: 'Equipment',           color: 'text-gray-700',   bg: 'bg-gray-100' },
  { value: 'transport',   label: 'Transport',           color: 'text-teal-700',   bg: 'bg-teal-100' },
  { value: 'biosecurity', label: 'Biosecurity',         color: 'text-green-700',  bg: 'bg-green-100' },
  { value: 'maintenance', label: 'Maintenance & Repairs', color: 'text-orange-700', bg: 'bg-orange-100' },
  { value: 'other',       label: 'Other',               color: 'text-gray-600',   bg: 'bg-gray-100' },
];

export const MOCK_EXPENSES: ExpenseRecord[] = [
  {
    id: 'exp-001', expenseNumber: 'EXP-2026-0091', date: '2026-09-03',
    farm: 'Idudi Poultry Farm', branch: 'Idudi Main Site',
    category: 'feed', description: 'Broiler Starter Mash (50 kg bags)', vendor: 'Ugachick Feeds Ltd',
    quantity: 80, unit: 'bags', unitCost: 125000, amount: 10000000,
    paymentMethod: 'Bank Transfer', receiptRef: 'UCF-2026-0812', approvedBy: 'Sarah Namukasa',
    status: 'paid', notes: '', recordedBy: 'Peter Waiswa',
  },
  {
    id: 'exp-002', expenseNumber: 'EXP-2026-0090', date: '2026-09-02',
    farm: 'Idudi Poultry Farm', branch: 'Idudi Main Site',
    category: 'medicine', description: 'Newcastle Disease Vaccine (1000 doses)', vendor: 'Agvet Uganda',
    quantity: 5, unit: 'vials', unitCost: 85000, amount: 425000,
    paymentMethod: 'Cash', receiptRef: 'AGV-0091', approvedBy: 'Sarah Namukasa',
    status: 'paid', notes: 'Stored in cold room immediately.', recordedBy: 'Sarah Namukasa',
  },
  {
    id: 'exp-003', expenseNumber: 'EXP-2026-0089', date: '2026-09-01',
    farm: 'Kampala Demonstration Farm', branch: 'Kawanda Layer Unit',
    category: 'labor', description: 'Monthly Wages — Farm Workers (Sep 2026)', vendor: 'Internal Payroll',
    quantity: 11, unit: 'employees', unitCost: 450000, amount: 4950000,
    paymentMethod: 'Mobile Money', receiptRef: 'PAY-SEP-2026', approvedBy: 'Dr. Grace Atim',
    status: 'paid', notes: '', recordedBy: 'Dr. Grace Atim',
  },
  {
    id: 'exp-004', expenseNumber: 'EXP-2026-0088', date: '2026-08-30',
    farm: 'Idudi Poultry Farm', branch: 'Idudi Extension Site',
    category: 'utilities', description: 'Electricity Bill — August 2026', vendor: 'UMEME Ltd',
    quantity: undefined, unit: undefined, unitCost: undefined, amount: 780000,
    paymentMethod: 'Bank Transfer', receiptRef: 'UMEME-AUG26', approvedBy: 'Peter Waiswa',
    status: 'paid', notes: '', recordedBy: 'Peter Waiswa',
  },
  {
    id: 'exp-005', expenseNumber: 'EXP-2026-0087', date: '2026-08-28',
    farm: 'Idudi Poultry Farm', branch: 'Idudi Main Site',
    category: 'biosecurity', description: 'Disinfectants & Sanitizers (Virkon S)', vendor: 'Farmcare Uganda',
    quantity: 20, unit: 'kg', unitCost: 45000, amount: 900000,
    paymentMethod: 'Cash', receiptRef: 'FC-0234', approvedBy: 'Sarah Namukasa',
    status: 'approved', notes: 'Quarterly biosecurity restocking.', recordedBy: 'Sarah Namukasa',
  },
  {
    id: 'exp-006', expenseNumber: 'EXP-2026-0086', date: '2026-08-25',
    farm: 'Kampala Demonstration Farm', branch: 'Kawanda Layer Unit',
    category: 'feed', description: 'Layer Mash (50 kg bags)', vendor: 'Ugachick Feeds Ltd',
    quantity: 60, unit: 'bags', unitCost: 130000, amount: 7800000,
    paymentMethod: 'Bank Transfer', receiptRef: 'UCF-2026-0798', approvedBy: 'Dr. Grace Atim',
    status: 'paid', notes: '', recordedBy: 'Dr. Grace Atim',
  },
  {
    id: 'exp-007', expenseNumber: 'EXP-2026-0085', date: '2026-08-22',
    farm: 'Idudi Poultry Farm', branch: 'Idudi Main Site',
    category: 'maintenance', description: 'Ventilation Fan Repair — House A1', vendor: 'Kampala Electrical Works',
    quantity: undefined, unit: undefined, unitCost: undefined, amount: 350000,
    paymentMethod: 'Cash', receiptRef: 'KEW-0112', approvedBy: 'Sarah Namukasa',
    status: 'paid', notes: 'Fan motor replaced.', recordedBy: 'Peter Waiswa',
  },
  {
    id: 'exp-008', expenseNumber: 'EXP-2026-0084', date: '2026-08-20',
    farm: 'Idudi Poultry Farm', branch: 'Idudi Extension Site',
    category: 'transport', description: 'Fuel — Delivery Truck (Aug)', vendor: 'Total Energies',
    quantity: 120, unit: 'litres', unitCost: 5200, amount: 624000,
    paymentMethod: 'Cash', receiptRef: 'TOTAL-AUG26', approvedBy: 'Peter Waiswa',
    status: 'paid', notes: '', recordedBy: 'Peter Waiswa',
  },
  {
    id: 'exp-009', expenseNumber: 'EXP-2026-0083', date: '2026-08-18',
    farm: 'Kampala Demonstration Farm', branch: 'Kawanda Layer Unit',
    category: 'medicine', description: 'Gumboro Vaccine (500 doses)', vendor: 'Agvet Uganda',
    quantity: 3, unit: 'vials', unitCost: 72000, amount: 216000,
    paymentMethod: 'Cash', receiptRef: 'AGV-0088', approvedBy: 'Dr. Grace Atim',
    status: 'pending', notes: 'Awaiting approval.', recordedBy: 'Dr. Grace Atim',
  },
  {
    id: 'exp-010', expenseNumber: 'EXP-2026-0082', date: '2026-08-15',
    farm: 'Idudi Poultry Farm', branch: 'Idudi Main Site',
    category: 'equipment', description: 'Automatic Drinkers (set of 20)', vendor: 'Poultry Supplies Uganda',
    quantity: 20, unit: 'units', unitCost: 35000, amount: 700000,
    paymentMethod: 'Bank Transfer', receiptRef: 'PSU-0441', approvedBy: 'Sarah Namukasa',
    status: 'paid', notes: '', recordedBy: 'Sarah Namukasa',
  },
];

export const EXPENSE_STATUS_STYLES: Record<ExpenseStatus, { bg: string; text: string; label: string }> = {
  pending:  { bg: 'bg-amber-100',  text: 'text-amber-700',  label: 'Pending' },
  approved: { bg: 'bg-blue-100',   text: 'text-blue-700',   label: 'Approved' },
  rejected: { bg: 'bg-red-100',    text: 'text-red-700',    label: 'Rejected' },
  paid:     { bg: 'bg-green-100',  text: 'text-green-700',  label: 'Paid' },
};

export const FARMS_LIST = [
  { id: 'farm-001', name: 'Idudi Poultry Farm', branches: ['Idudi Main Site', 'Idudi Extension Site'] },
  { id: 'farm-002', name: 'Kampala Demonstration Farm', branches: ['Kawanda Layer Unit'] },
  { id: 'farm-003', name: 'Mbarara Broiler Farm', branches: [] },
];

export const PAYMENT_METHODS = ['Cash', 'Mobile Money', 'Bank Transfer', 'Cheque'];

export function formatUGX(amount: number): string {
  return 'UGX ' + amount.toLocaleString();
}
